import React, { Fragment, useState, useEffect } from "react";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";
import FilePreview from "../Common/FilePreview";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";
import { useSelector } from "react-redux";

const TCReportList = () => {
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [reports, setReports] = useState([]);
 const [selectedReport, setSelectedReport] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 });

 const [searchParams] = useSearchParams();
 const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
 const canManageTCs =
 currentRole === "admin" ||
 Boolean(
 user?.permissions?.some((permission) =>
 ["create:tcs", "edit:tcs", "approve:tcs", "reject:tcs", "delete:tcs"].includes(permission)
 )
 );

 const workOrderFromUrl = searchParams.get("workOrder") || "";
 const projectFromUrl = searchParams.get("project") || "";

 // ============================================================
 // FETCH TC REPORTS
 // ============================================================
 const fetchReports = async () => {
 try {
 const params = {
 ...(projectFromUrl && { project: projectFromUrl }),
 ...(workOrderFromUrl && { workOrder: workOrderFromUrl }),
 page: pagination.currentPage,
 limit: pagination.limit,
 };

 const res = await api.get("/testing-commissioning", { params });

 if (res.data.success) {
 if (res.data.pagination) {
 setPagination(prev => ({
 ...prev,
 ...res.data.pagination
 }));
 }
 const mapped = res.data.data.map((item) => {
 const project = item.project || {};
 const wo = item.workOrder || {};

 let finalStatus = "Pending";
 if (item.items?.some((i) => i.status === "NC Raised"))
 finalStatus = "NC Raised";
 else if (item.items?.some((i) => i.status === "Retest Required"))
 finalStatus = "Retest Required";
 else if (item.items?.every((i) => i.status === "Approved"))
 finalStatus = "Approved";

 return {
 id: item._id,
 tcNo: item.tcNumber,
 projectName: `${wo.woNo || "WO-N/A"} - ${
 project.projectName ||
 project.project ||
 project.name ||
 "No Name"
 }`,
 testDate: item.testDate ? item.testDate.split("T")[0] : "",
 submittedBy: item.submittedBy?.name || "Unknown",
 status: finalStatus,
 items: item.items || [],
 raw: item,
 };
 });

 setReports(mapped);
 } else {
 swal("Error", res.data.message, "error");
 }
 } catch (error) {
 swal("Error", error.message, "error");
 }
 };

 useEffect(() => {
 fetchReports();
 }, [workOrderFromUrl, projectFromUrl, pagination.currentPage]);

 const handlePageChange = (page) => {
 setPagination(prev => ({ ...prev, currentPage: page }));
 };

 // ============================================================
 // DELETE REPORT
 // ============================================================
 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Delete?",
 text: "This action cannot be undone!",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/testing-commissioning/${id}`);

 if (res.data.success) {
 setReports((prev) => prev.filter((r) => r.id !== id));
 swal("Deleted!", "TC Report removed.", "success");
 } else {
 swal("Error", res.data.message, "error");
 }
 } catch (error) {
 swal("Error", error.message, "error");
 }
 };

 // ============================================================
 // VIEW MODAL
 // ============================================================
 const handleView = (report) => {
 setSelectedReport(report.raw);
 setShowModal(true);
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000" cx="19" cy="12" r="2"></circle>
 </svg>
 );

 const getBadgeColor = (status) => {
 switch (status) {
 case "Pending":
 return "warning";
 case "Approved":
 return "success";
 case "NC Raised":
 return "danger";
 case "Retest Required":
 return "info";
 default:
 return "secondary";
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="TC Reports" motherMenu="ITC" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">TC Report List</Card.Title>
 <div className="d-flex gap-2">
 <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (window.history.length > 1 && (!window.history.state || window.history.state.idx > 0)) {
                    navigate(-1);
                  } else {
                    const query = projectFromUrl
                      ? `?project=${projectFromUrl}`
                      : workOrderFromUrl
                      ? `?workOrder=${workOrderFromUrl}`
                      : "";
                    navigate(`/work-orders${query}`);
                  }
                }}
              >
                <i className="fas fa-arrow-left me-1"></i>
                Back
              </Button>
 </div>
 </Card.Header>

 <Card.Body>
 <div className="table-responsive" style={{ maxHeight: "700px", overflowY: "auto" }}>
 <Table hover className="align-middle">
 <thead className="sticky-top bg-white">
 <tr>
 <th>#</th>
 <th>TC No</th>
 <th>Project</th>
 <th>Work Order</th>
 <th>Equipment Tested</th>
 <th>Test Date</th>
 <th>Submitted By</th>
 <th>Status</th>
 <th className="text-center">Verified Copy</th>
 <th className="text-center">Actions</th>
 </tr>
 </thead>

 <tbody>
 {reports.length === 0 ? (
 <tr>
 <td colSpan={10} className="text-center py-5 text-muted">
 No TC Reports found.
 </td>
 </tr>
 ) : (
 reports.map((r, i) => (
 <tr key={r.id}>
 <td>{(pagination.currentPage - 1) * pagination.limit + (i + 1)}</td>
 <td className="text-nowrap font-weight-bold">{r.tcNo}</td>
 <td>{r.project}</td>
 <td>{r.workOrder}</td>

 {/* SHOW EQUIPMENT */}
 <td style={{ maxWidth: "300px" }}>
 <div style={{ maxHeight: "100px", overflowY: "auto", paddingRight: "5px" }}>
 {r.items.length > 0 ? (
 <ul className="list-unstyled mb-0">
 {r.items.map((it, idx) => (
 <li key={idx} className="mb-1 border-bottom pb-1 last-child-no-border small">
 <i className="fas fa-check-circle text-success me-2"></i>
 <strong>{it.equipmentName}</strong>
 {it.unit ? ` (${it.unit})` : ""}
 </li>
 ))}
 </ul>
 ) : (
 <span className="text-muted small">N/A</span>
 )}
 </div>
 </td>

 <td className="text-nowrap">{r.testDate}</td>
 <td className="text-nowrap small">{r.submittedBy}</td>

 {/* OVERALL STATUS */}
 <td>
 <Badge bg={getBadgeColor(r.status)}>
 {r.status}
 </Badge>
 </td>

 <td className="text-center">
 <VerifiedCopyActions 
 id={r.id} 
 type="testing-commissioning" 
 hasVerifiedCopy={!!r.raw?.verifiedCopy?.filePath} 
 verifiedCopy={r.raw?.verifiedCopy}
 onUploadSuccess={fetchReports}
 canUpload={canManageTCs}
 />
 </td>

 <td className="text-center">
 <Dropdown>
 <Dropdown.Toggle
 variant=""
 as="div"
 className="btn-link i-false p-0 cursor-pointer"
 title="Click for more actions"
 >
 {svgMenu}
 </Dropdown.Toggle>

 <Dropdown.Menu align="end" as={PortalMenu}>
 <Dropdown.Item 
 onClick={() => handleView(r)}
 title="View full TC report details"
 >
 <i className="fas fa-eye me-2 text-primary"></i>
 View Details
 </Dropdown.Item>

 {r.raw?.testReport?.fileUrl && (
 <Dropdown.Item
 href={getFileUrl(r.raw.testReport.fileUrl)}
 target="_blank"
 rel="noopener noreferrer"
 title="View system-generated report file"
 >
 <i className="fas fa-file-alt me-2 text-info"></i>
 View Report
 </Dropdown.Item>
 )}

 <Dropdown.Item
 as={Link}
 to={
 workOrderFromUrl
 ? `/tc-report/edit/${r.id}?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : `/tc-report/edit/${r.id}`
 }
 title="Edit TC report"
 >
 <i className="fas fa-edit me-2 text-warning"></i>
 Edit
 </Dropdown.Item>

 <Dropdown.Divider />
 <Dropdown.Item
 onClick={() => handleDelete(r.id)}
 className="text-danger"
 title="Delete this report"
 >
 <i className="fas fa-trash me-2"></i>
 Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </Table>
 {/* Pagination */}
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={10}
 limit={pagination.limit}
 onPageChange={handlePageChange}
 />
 </div>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ============================================================
 VIEW MODAL
 ============================================================ */}
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>TC Report Details</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {selectedReport ? (
 <>
 <p><strong>TC No:</strong> {selectedReport.tcNumber}</p>

 <p>
 <strong>Project:</strong>{" "}
 {selectedReport.workOrder?.woNo} - {
 selectedReport.workOrder?.project?.projectName ||
 selectedReport.workOrder?.project?.project ||
 selectedReport.workOrder?.project?.name ||
 "N/A"
}

 </p>

 <h5 className="mt-3">Equipment Tested</h5>
 <Table bordered>
 <thead>
 <tr>
 <th>#</th>
 <th>Equipment</th>
 <th>Description</th>
 <th>Unit</th>
 <th>Test Performed</th>
 <th>Result</th>
 <th>Status</th>
 </tr>
 </thead>

 <tbody>
 {selectedReport.items?.length > 0 ? (
 selectedReport.items.map((item, idx) => (
 <tr key={idx}>
 <td>{idx + 1}</td>
 <td>{item.equipmentName}</td>
 <td>{item.description || "-"}</td>
 <td>{item.unit || "-"}</td>
 <td>{item.testPerformed}</td>
 <td>{item.testResults}</td>
 <td>
 <Badge bg={getBadgeColor(item.status)}>
 {item.status}
 </Badge>
 </td>
 </tr>
 ))
 ) : (
 <tr>
 <td colSpan="7" className="text-center">
 No items found
 </td>
 </tr>
 )}
 </tbody>
 </Table>

 <p><strong>Test Date:</strong> {selectedReport.testDate?.split("T")[0]}</p>

 {/* OVERALL STATUS */}
 <p>
 <strong>Status: </strong>
 <Badge bg={getBadgeColor(
 selectedReport.items?.every(i => i.status === "Approved")
 ? "Approved"
 : selectedReport.items?.some(i => i.status === "NC Raised")
 ? "NC Raised"
 : selectedReport.items?.some(i => i.status === "Retest Required")
 ? "Retest Required"
 : "Pending"
 )}>
 {
 selectedReport.items?.every(i => i.status === "Approved")
 ? "Approved"
 : selectedReport.items?.some(i => i.status === "NC Raised")
 ? "NC Raised"
 : selectedReport.items?.some(i => i.status === "Retest Required")
 ? "Retest Required"
 : "Pending"
 }
 </Badge>
 </p>

 <p><strong>Submitted By:</strong> {selectedReport.submittedBy?.name || "N/A"}</p>

 {selectedReport.testReport?.fileUrl && (
 <div className="mt-3">
 <strong>Report File:</strong>
 <FilePreview 
 file={{
 fileName: selectedReport.testReport.fileName,
 fileUrl: selectedReport.testReport.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Report?",
 text: "This will remove the test report file.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/testing-commissioning/${selectedReport._id}/remove-file`, { field: 'testReport' });
 if (res.data.success) {
 swal("Deleted!", "Report removed", "success");
 handleView({ raw: selectedReport }); // Refresh modal
 }
 } catch (err) {
 swal("Error", "Failed to remove report", "error");
 }
 }}
 />
 </div>
 )}
 </>
 ) : (
 <p>No report selected.</p>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default TCReportList;
